import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, Subject, catchError, map, merge, of, shareReplay, switchMap, timer } from 'rxjs';

import { EnvironmentBookingServiceClient } from '../../generated.commonjs/Environment_bookingServiceClientPb';
import {
  GetEnvironmentBookingRequest,
  GetEnvironmentBookingResponse,
} from '../../generated.commonjs/environment_booking_pb';
import { AuthInterceptor } from './auth.interceptor';
import { ClientLoggingService } from './client-logging.service';
import { EnvironmentService } from './environment.service';

export type EnvironmentBookingState = 'free' | 'reserved' | 'expired' | 'stale' | 'malformed' | 'unavailable';

/**
 * Why the booking could not be read. The four cases need different people to act, so the control
 * names them instead of reporting every failure as "unavailable" (Issue #2491):
 * - `sign-in`: the browser could not get a sign-in token, so no request was sent.
 * - `not-permitted`: the server decided this account may not see booking details.
 * - `permission-check-failed`: the server could not make that decision at all, for example because
 *   it cannot read the environment's access group. Only an administrator can fix this.
 * - `unreachable`: anything else, such as a network failure or a server error.
 */
export type EnvironmentBookingProblem = 'sign-in' | 'not-permitted' | 'permission-check-failed' | 'unreachable';

export interface EnvironmentBooking {
  environment: string;
  state: EnvironmentBookingState;
  observedAt: string;
  generation: string;
  leaseId: string;
  acquiredAt: string;
  renewedAt: string;
  expiresAt: string;
  repository: string;
  issue: string;
  issueTitle: string;
  profile: string;
  attempt: string;
  candidateSha: string;
  phase: string;
  standInBy: string;
  ownershipKnown: boolean;
  waitersPresent: boolean;
  detail: string;
  /** Set only when the latest read failed; absent on every answer the server gave. */
  problem?: EnvironmentBookingProblem;
}

type BookingClient = Pick<EnvironmentBookingServiceClient, 'getEnvironmentBooking'>;
type BookingClientFactory = (endpoint: string, interceptor: AuthInterceptor) => BookingClient;

export const ENVIRONMENT_BOOKING_CLIENT_FACTORY = new InjectionToken<BookingClientFactory>(
  'ENVIRONMENT_BOOKING_CLIENT_FACTORY',
  {
    providedIn: 'root',
    factory: () => (endpoint, interceptor) =>
      new EnvironmentBookingServiceClient(endpoint, null, { unaryInterceptors: [interceptor] }),
  }
);

/** Polls the authenticated own-environment booking projection. */
@Injectable({ providedIn: 'root' })
export class EnvironmentBookingService {
  private environment = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private clientFactory = inject(ENVIRONMENT_BOOKING_CLIENT_FACTORY);
  private client: BookingClient | null = null;
  private lastConfirmed: EnvironmentBooking | null = null;
  private refreshRequests = new Subject<void>();

  /**
   * The disabled path is deliberately inert: it creates no gRPC client and issues no request.
   * AppComponent also omits the banner component entirely when this is false.
   */
  watchBooking(): Observable<EnvironmentBooking> {
    if (!this.environment.isEnvironmentBookingEnabled()) {
      return of();
    }
    // A requested refresh restarts the 30-second poll, so it is never followed at once by a
    // scheduled read.
    return merge(of(undefined), this.refreshRequests).pipe(
      switchMap(() => timer(0, 30_000)),
      switchMap(() =>
        this.read().pipe(
          map(booking => {
            if (booking.state === 'free' || booking.state === 'reserved' || booking.state === 'expired') {
              this.lastConfirmed = booking;
            }
            return booking;
          }),
          catchError(error => {
            ClientLoggingService.log('WARN', '[EnvironmentBooking] Booking projection request failed:', error);
            const problem = EnvironmentBookingService.classify(error);
            // A permission answer says nothing about the lease, but it does say the earlier details
            // must no longer be shown to this account.
            if (this.lastConfirmed && problem === 'unreachable') {
              return of({
                ...this.lastConfirmed,
                state: 'stale' as const,
                detail: 'These details are from the last successful check. The latest check did not get an answer.',
                problem,
              });
            }
            return of(this.unavailable(problem));
          })
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /** Reads the booking again now, instead of waiting for the next 30-second poll. */
  refresh(): void {
    this.refreshRequests.next();
  }

  private static classify(error: unknown): EnvironmentBookingProblem {
    const failure = error as { name?: string; code?: number; message?: string } | null;
    if (failure?.name === 'RpcAuthenticationError' || failure?.code === 16) {
      return 'sign-in';
    }
    if (failure?.code === 7) {
      // The server uses two descriptions for PERMISSION_DENIED: one when it decided against the
      // caller, one when it could not decide ("Environment booking authorization is unavailable").
      return /authorization is unavailable/i.test(failure.message ?? '') ? 'permission-check-failed' : 'not-permitted';
    }
    return 'unreachable';
  }

  private static problemDetail(problem: EnvironmentBookingProblem, environment: string): string {
    switch (problem) {
      case 'sign-in':
        return 'Your sign-in could not be confirmed, so the booking was not requested. Sign in again, then check again.';
      case 'not-permitted':
        return `Your account is not in the access group for ${environment}, so its booking details are hidden.`;
      case 'permission-check-failed':
        return (
          `The server could not check whether your account may see who is using ${environment}. ` +
          'It was unable to read the environment\u2019s access group, which an administrator has to fix.'
        );
      default:
        return `The booking service did not answer. Whether ${environment} is in use is unknown.`;
    }
  }

  private read(): Observable<EnvironmentBooking> {
    if (!this.client) {
      const apiServer = this.environment.getApiServer();
      this.client = this.clientFactory(`https://${apiServer}:443`, this.authInterceptor);
    }
    return new Observable<EnvironmentBooking>(observer => {
      const request = new GetEnvironmentBookingRequest();
      this.client!.getEnvironmentBooking(request, null)
        .then(response => {
          observer.next(EnvironmentBookingService.toBooking(response));
          observer.complete();
        })
        .catch(error => observer.error(error));
    });
  }

  private unavailable(problem: EnvironmentBookingProblem): EnvironmentBooking {
    const environment = this.environment.getEnvironmentBookingSlot();
    return {
      environment,
      state: 'unavailable',
      observedAt: '',
      generation: '',
      leaseId: '',
      acquiredAt: '',
      renewedAt: '',
      expiresAt: '',
      repository: '',
      issue: '',
      issueTitle: '',
      profile: '',
      attempt: '',
      candidateSha: '',
      phase: 'unknown',
      standInBy: '',
      ownershipKnown: false,
      waitersPresent: false,
      detail: EnvironmentBookingService.problemDetail(problem, environment),
      problem,
    };
  }

  private static toBooking(response: GetEnvironmentBookingResponse): EnvironmentBooking {
    const state = response.getState() as EnvironmentBookingState;
    return {
      environment: response.getEnvironment(),
      state,
      observedAt: response.getObservedAt(),
      generation: response.getGeneration(),
      leaseId: response.getLeaseId(),
      acquiredAt: response.getAcquiredAt(),
      renewedAt: response.getRenewedAt(),
      expiresAt: response.getExpiresAt(),
      repository: response.getRepository(),
      issue: response.getIssue(),
      issueTitle: response.getIssueTitle(),
      profile: response.getProfile(),
      attempt: response.getAttempt(),
      candidateSha: response.getCandidateSha(),
      phase: response.getPhase(),
      standInBy: response.getStandInBy(),
      ownershipKnown: response.getOwnershipKnown(),
      waitersPresent: response.getWaitersPresent(),
      detail: response.getDetail(),
    };
  }
}
